/**
 * Wholesale inquiry form — intl-tel-input + validation.
 * Initializes only when <gr-wholesale-form> exists on the page.
 */
(function () {
  const ITI_CDN = 'https://cdn.jsdelivr.net/npm/intl-tel-input@25.2.1';

  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      if (document.querySelector('script[src="' + src + '"]')) {
        resolve();
        return;
      }
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  function loadCss(href) {
    if (document.querySelector('link[href="' + href + '"]')) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    document.head.appendChild(link);
  }

  class GrWholesaleForm extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      loadCss(ITI_CDN + '/build/css/intlTelInput.min.css');
      loadScript(ITI_CDN + '/build/js/intlTelInput.min.js')
        .then(() => this.init())
        .catch(() => {
          /* Phone input still works as a plain tel field if CDN fails */
        });
    }

    init() {
      this.form = this.querySelector('form');
      if (!this.form || typeof window.intlTelInput !== 'function') return;

      const countrySelect = this.querySelector('[data-wholesale-country-select]');
      const phoneInput = this.querySelector('[data-wholesale-phone-input]');
      if (!countrySelect || !phoneInput) return;

      const initialFromStore = (this.dataset.initialCountry || 'us').toLowerCase();
      const allCountries = window.intlTelInput.getCountryData().slice();
      allCountries.sort(function (a, b) {
        return a.name.localeCompare(b.name);
      });

      countrySelect.innerHTML = '';
      const placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = 'Select a country';
      countrySelect.appendChild(placeholder);

      allCountries.forEach(function (c) {
        const opt = document.createElement('option');
        opt.value = c.iso2.toUpperCase();
        opt.textContent = c.name;
        countrySelect.appendChild(opt);
      });

      let initial = initialFromStore;
      if (!allCountries.some(function (c) {
        return c.iso2 === initial;
      })) {
        initial = 'us';
      }
      countrySelect.value = initial.toUpperCase();

      const utilsUrl = ITI_CDN + '/build/js/utils.js';
      const iti = window.intlTelInput(phoneInput, {
        loadUtils: function () {
          return import(utilsUrl);
        },
        initialCountry: initial,
        separateDialCode: true,
        preferredCountries: ['us', 'au', 'gb'],
        countrySearch: true,
        strictMode: false,
        validationNumberTypes: null,
        containerClass: 'iti-wholesale',
      });

      this.iti = iti;

      countrySelect.addEventListener('change', function () {
        const v = countrySelect.value;
        if (v) iti.setCountry(v.toLowerCase());
      });

      phoneInput.addEventListener('countrychange', function () {
        const data = iti.getSelectedCountryData();
        if (data && data.iso2) {
          countrySelect.value = data.iso2.toUpperCase();
        }
      });

      const checkboxes = this.querySelectorAll('[data-wholesale-referral-option]');
      const rootEl = this;

      this.form.addEventListener('submit', function (e) {
        const consentCb = rootEl.querySelector('[data-wholesale-terms-consent]');
        if (!consentCb || !consentCb.checked) {
          e.preventDefault();
          if (consentCb) consentCb.focus();
          alert('Please agree to the Wholesale Terms and Conditions to submit this form.');
          return;
        }

        const checkedBoxes = Array.prototype.slice.call(checkboxes).filter(function (cb) {
          return cb.checked;
        });
        if (checkedBoxes.length === 0) {
          e.preventDefault();
          alert('Please select at least one option for "How did you hear about us?"');
          return;
        }

        const referralField = rootEl.querySelector('[data-wholesale-referral-field]');
        if (referralField) {
          referralField.value = checkedBoxes
            .map(function (cb) {
              return cb.value;
            })
            .join(' | ');
        }

        if (!iti.isValidNumber()) {
          e.preventDefault();
          alert('Please enter a valid phone number.');
          return;
        }

        phoneInput.value = iti.getNumber();
        rootEl.form.setAttribute('method', 'post');
        rootEl.form.removeAttribute('novalidate');

        const submitBtn = rootEl.querySelector('[data-wholesale-submit]');
        rootEl.classList.add('is-submitting');
        if (submitBtn) {
          submitBtn.disabled = true;
          submitBtn.setAttribute('aria-busy', 'true');
        }
      });
    }
  }

  if (!customElements.get('gr-wholesale-form')) {
    customElements.define('gr-wholesale-form', GrWholesaleForm);
  }
})();
